import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schemaEndpoints } from '@/lib/api/schemas';
import { apiClient } from '@/lib/api/client';
import type {
  Schema,
  CreateSchemaRequest,
  UpdateSchemaRequest,
  CreateSchemaVersionRequest,
} from '../types/schema.types';

/**
 * Query keys for schema-related queries
 * Centralized key factory for consistent cache management
 */
export const schemaKeys = {
  all: ['schemas'] as const,
  lists: () => [...schemaKeys.all, 'list'] as const,
  list: (params?: { page?: number; size?: number; search?: string }) =>
    [...schemaKeys.lists(), params] as const,
  details: () => [...schemaKeys.all, 'detail'] as const,
  detail: (id: string) => [...schemaKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated list of schemas
 *
 * Provides automatic caching, background refetching, and loading states
 * for schema list data.
 *
 * @param params - Optional query parameters
 * @param params.page - Page number (1-indexed, default: 1)
 * @param params.size - Items per page (default: 20)
 * @param params.search - Search filter by schema name
 *
 * @example
 * ```tsx
 * function SchemaList() {
 *   const { data, isLoading, error } = useSchemas({ page: 1, size: 20 });
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorAlert error={error} />;
 *
 *   return (
 *     <div>
 *       {data.items.map(schema => (
 *         <SchemaCard key={schema.id} schema={schema} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useSchemas = (params?: { page?: number; size?: number; search?: string }) => {
  return useQuery({
    queryKey: schemaKeys.list(params),
    queryFn: async () => {
      const response = await schemaEndpoints.list(params);
      return response.data;
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to fetch detailed information for a single schema
 *
 * Automatically fetches schema details including all fields, versions,
 * and associated datasets.
 *
 * @param id - Schema UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if id provided)
 *
 * @example
 * ```tsx
 * function SchemaDetails({ schemaId }: { schemaId: string }) {
 *   const { data: schema, isLoading } = useSchema(schemaId);
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <div>
 *       <h1>{schema.name}</h1>
 *       <FieldTable fields={schema.fields} />
 *       <DatasetList datasets={schema.datasets} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useSchema = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: schemaKeys.detail(id),
    queryFn: async () => {
      const response = await schemaEndpoints.get(id);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 60000, // Consider data fresh for 60 seconds
  });
};

/**
 * Hook to create a new schema
 *
 * Provides optimistic updates and automatic cache invalidation.
 * On success, invalidates schema list queries to show the new schema.
 *
 * @example
 * ```tsx
 * function CreateSchemaForm() {
 *   const createSchema = useCreateSchema();
 *
 *   const handleSubmit = async (data: CreateSchemaRequest) => {
 *     try {
 *       const schema = await createSchema.mutateAsync(data);
 *       toast.success(`Schema "${schema.name}" created successfully`);
 *       navigate(`/schemas/${schema.id}`);
 *     } catch (error) {
 *       toast.error('Failed to create schema');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <SchemaFieldEditor />
 *       <Button loading={createSchema.isPending}>Create Schema</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export const useCreateSchema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSchemaRequest) => {
      const response = await schemaEndpoints.create(data);
      return response.data;
    },
    onSuccess: (newSchema) => {
      // Invalidate all list queries to fetch updated data
      queryClient.invalidateQueries({ queryKey: schemaKeys.lists() });

      // Optimistically add the new schema to cache
      queryClient.setQueryData<Schema>(schemaKeys.detail(newSchema.id), newSchema);
    },
  });
};

/**
 * Hook to update an existing schema
 *
 * Provides optimistic updates for immediate UI feedback.
 * Automatically invalidates related queries on success.
 *
 * @example
 * ```tsx
 * function EditSchemaForm({ schema }: { schema: Schema }) {
 *   const updateSchema = useUpdateSchema();
 *
 *   const handleSubmit = async (data: UpdateSchemaRequest) => {
 *     try {
 *       await updateSchema.mutateAsync({ id: schema.id, data });
 *       toast.success('Schema updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update schema');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Input name="description" defaultValue={schema.description} />
 *       <Button loading={updateSchema.isPending}>Save Changes</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export const useUpdateSchema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSchemaRequest }) => {
      const response = await schemaEndpoints.update(id, data);
      return response.data;
    },
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: schemaKeys.detail(id) });

      // Snapshot previous value for rollback
      const previousSchema = queryClient.getQueryData<Schema>(schemaKeys.detail(id));

      // Optimistically update cache
      if (previousSchema) {
        queryClient.setQueryData<Schema>(schemaKeys.detail(id), {
          ...previousSchema,
          ...data,
        });
      }

      return { previousSchema };
    },
    onError: (_error, { id }, context) => {
      // Rollback on error
      if (context?.previousSchema) {
        queryClient.setQueryData(schemaKeys.detail(id), context.previousSchema);
      }
    },
    onSuccess: (updatedSchema, { id }) => {
      // Update detail cache with server response
      queryClient.setQueryData(schemaKeys.detail(id), updatedSchema);

      // Invalidate list queries to reflect changes
      queryClient.invalidateQueries({ queryKey: schemaKeys.lists() });
    },
  });
};

/**
 * Hook to delete a schema
 *
 * Removes schema from cache on success and invalidates list queries.
 * WARNING: Deletion will fail if schema is referenced by datasets.
 *
 * @example
 * ```tsx
 * function DeleteSchemaButton({ schemaId }: { schemaId: string }) {
 *   const deleteSchema = useDeleteSchema();
 *
 *   const handleDelete = async () => {
 *     if (!confirm('Are you sure? This cannot be undone.')) return;
 *
 *     try {
 *       await deleteSchema.mutateAsync(schemaId);
 *       toast.success('Schema deleted successfully');
 *       navigate('/schemas');
 *     } catch (error) {
 *       toast.error('Failed to delete schema. It may be in use by datasets.');
 *     }
 *   };
 *
 *   return (
 *     <Button variant="destructive" onClick={handleDelete} loading={deleteSchema.isPending}>
 *       Delete Schema
 *     </Button>
 *   );
 * }
 * ```
 */
export const useDeleteSchema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await schemaEndpoints.delete(id);
      return id;
    },
    onSuccess: (deletedId) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: schemaKeys.detail(deletedId) });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: schemaKeys.lists() });
    },
  });
};

/**
 * Hook to create a new version of an existing schema
 *
 * Creates a new schema version while maintaining backward/forward compatibility
 * based on the schema's compatibility mode.
 *
 * @example
 * ```tsx
 * function CreateVersionForm({ schemaId }: { schemaId: string }) {
 *   const createVersion = useCreateSchemaVersion();
 *   const { data: schema } = useSchema(schemaId);
 *
 *   const handleSubmit = async (data: CreateSchemaVersionRequest) => {
 *     try {
 *       const updatedSchema = await createVersion.mutateAsync({ id: schemaId, data });
 *       toast.success(`Version ${updatedSchema.version} created`);
 *     } catch (error) {
 *       toast.error('Compatibility check failed. Review your changes.');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <CompatibilityModeInfo mode={schema.compatibility_mode} />
 *       <SchemaFieldEditor fields={schema.fields} />
 *       <Button loading={createVersion.isPending}>Create Version</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export const useCreateSchemaVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CreateSchemaVersionRequest }) => {
      const response = await schemaEndpoints.createVersion(id, data);
      return response.data;
    },
    onSuccess: (updatedSchema, { id }) => {
      // Update detail cache with new version
      queryClient.setQueryData(schemaKeys.detail(id), updatedSchema);

      // Invalidate list queries to show updated version number
      queryClient.invalidateQueries({ queryKey: schemaKeys.lists() });
    },
  });
};

/**
 * Hook to infer schema from an uploaded file
 *
 * Analyzes file content to automatically detect field types, nullable constraints,
 * and potential PII fields. Supports Parquet, CSV, and JSON formats.
 *
 * @example
 * ```tsx
 * function SchemaInferenceStep() {
 *   const inferSchema = useInferSchema();
 *   const schemaStore = useSchemaCreationStore();
 *
 *   const handleFileUpload = async (file: File) => {
 *     try {
 *       const result = await inferSchema.mutateAsync(file);
 *
 *       // Store inferred fields in wizard state
 *       schemaStore.setInferredFields(result.fields);
 *       schemaStore.nextStep();
 *
 *       if (result.detected_pii.length > 0) {
 *         toast.warning(`PII detected: ${result.detected_pii.join(', ')}`);
 *       }
 *     } catch (error) {
 *       toast.error('Failed to infer schema from file');
 *     }
 *   };
 *
 *   return (
 *     <FileDropzone
 *       accept={{ 'application/octet-stream': ['.parquet'], 'text/csv': ['.csv'] }}
 *       onDrop={(files) => handleFileUpload(files[0])}
 *       disabled={inferSchema.isPending}
 *     />
 *   );
 * }
 * ```
 */
export const useInferSchema = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const response = await schemaEndpoints.inferFromFile(file);
      return response.data;
    },
  });
};

/**
 * Request body for linking a dataset to a schema library entry
 */
interface LinkDatasetToSchemaRequest {
  schema_library_id: string;
  force?: boolean;
}

/**
 * Hook to link a dataset to a schema library entry
 *
 * Associates a dataset with a schema from the schema library.
 * Use the `force` option to override an existing schema link.
 *
 * Automatically invalidates both dataset and schema query caches on success
 * so that linked-dataset lists and dataset-detail views stay in sync.
 *
 * @example
 * ```tsx
 * function LinkSchemaButton({ datasetId, schemaId }: Props) {
 *   const linkSchema = useLinkDatasetToSchema();
 *   const { toast } = useToast();
 *
 *   const handleLink = async () => {
 *     try {
 *       await linkSchema.mutateAsync({
 *         datasetId,
 *         schemaLibraryId: schemaId,
 *       });
 *       toast({ title: 'Schema linked successfully' });
 *     } catch (error) {
 *       toast({
 *         title: 'Failed to link schema',
 *         description: error instanceof Error ? error.message : 'Unknown error',
 *         variant: 'destructive',
 *       });
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleLink} loading={linkSchema.isPending}>
 *       Link Schema
 *     </Button>
 *   );
 * }
 * ```
 */
export const useLinkDatasetToSchema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      datasetId,
      schemaLibraryId,
      force,
    }: {
      datasetId: string;
      schemaLibraryId: string;
      force?: boolean;
    }) => {
      const body: LinkDatasetToSchemaRequest = {
        schema_library_id: schemaLibraryId,
        force,
      };
      const response = await apiClient.post(`/datasets/${datasetId}/schema/link`, body);
      return response.data;
    },
    onSuccess: (_data, { datasetId }) => {
      // Invalidate dataset queries so schema info is refreshed
      queryClient.invalidateQueries({ queryKey: ['datasets', datasetId] });
      queryClient.invalidateQueries({ queryKey: ['datasets', datasetId, 'schema'] });

      // Invalidate schema queries so linked-dataset counts update
      queryClient.invalidateQueries({ queryKey: schemaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemaKeys.details() });
    },
  });
};

/**
 * Hook to unlink a dataset from its current schema library entry
 *
 * Removes the association between a dataset and its linked schema.
 * The dataset's actual data is not modified -- only the library link is removed.
 *
 * Automatically invalidates both dataset and schema query caches on success.
 *
 * @example
 * ```tsx
 * function UnlinkSchemaButton({ datasetId }: { datasetId: string }) {
 *   const unlinkSchema = useUnlinkDatasetFromSchema();
 *   const { toast } = useToast();
 *
 *   const handleUnlink = async () => {
 *     try {
 *       await unlinkSchema.mutateAsync(datasetId);
 *       toast({ title: 'Schema unlinked successfully' });
 *     } catch (error) {
 *       toast({
 *         title: 'Failed to unlink schema',
 *         description: error instanceof Error ? error.message : 'Unknown error',
 *         variant: 'destructive',
 *       });
 *     }
 *   };
 *
 *   return (
 *     <Button variant="outline" onClick={handleUnlink} loading={unlinkSchema.isPending}>
 *       Unlink Schema
 *     </Button>
 *   );
 * }
 * ```
 */
export const useUnlinkDatasetFromSchema = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (datasetId: string) => {
      const response = await apiClient.post(`/datasets/${datasetId}/schema/unlink`);
      return response.data;
    },
    onSuccess: (_data, datasetId) => {
      // Invalidate dataset queries so schema info is refreshed
      queryClient.invalidateQueries({ queryKey: ['datasets', datasetId] });
      queryClient.invalidateQueries({ queryKey: ['datasets', datasetId, 'schema'] });

      // Invalidate schema queries so linked-dataset counts update
      queryClient.invalidateQueries({ queryKey: schemaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemaKeys.details() });
    },
  });
};
